import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, Grid,
  RadioGroup, FormControlLabel, Radio, Checkbox, TextField, Autocomplete, CircularProgress, alpha,
} from "@mui/material";
import PersonAddAlt1OutlinedIcon from "@mui/icons-material/PersonAddAlt1Outlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { useGetClassEligibleStudentsQuery, useAssignStudentToClassMutation } from "../../../Api/attoDeskApi";
import { useNotifier } from "../../../Core/Notifier";
import AppDatePicker from "../../../Components/DatePicker/AppDatePicker";
import dayjs from "dayjs";
import {
  buildEligibleStudentOptions,
  filterEligibleStudents,
  EligibleStudentOption,
} from "./eligibleStudentOptions";
import GradeSubjectGroups from "./GradeSubjectGroups";

const Ctx: React.FC<{ label: string; value?: string | number | null }> = ({ label, value }) => (
  <Box>
    <Typography sx={{ fontSize: 10, fontWeight: 700, color: "text.secondary", textTransform: "uppercase" }}>{label}</Typography>
    <Typography sx={{ fontSize: 12.5, fontWeight: 600 }}>{value ?? "—"}</Typography>
  </Box>
);

/**
 * Dedicated Assign Student flow. Inherits the class context (staff/subject/grade/
 * mode/location/day-time/capacity/enrollment) read-only; only asks for student,
 * effective date, optional fee override and scope. Eligible students are grade-
 * filtered by the backend (an authorized grade override lifts the filter). The
 * backend re-enforces grade/capacity/duplicate/conflict/status on submit.
 *
 * Student picking mirrors the stable Class Schedule list filter (index.tsx +
 * filterOptions.ts): the eligible list is loaded ONCE per (class, grade-override)
 * and searched CLIENT-SIDE. The dropdown's input text is never fed back into the
 * server query, so selecting a student cannot trigger a refetch/reconciliation
 * loop. The selection is held as a full option object (not just an id resolved
 * against a churning list) so it stays visible across background refreshes.
 */
const AssignStudentDialog: React.FC<{
  open: boolean;
  row: any;
  canOverrideGrade: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}> = ({ open, row, canOverrideGrade, onClose, onSuccess }) => {
  const { showMessage, showErrorMessage } = useNotifier();
  const [assignStudent, { isLoading: isAssigning }] = useAssignStudentToClassMutation();

  const [selected, setSelected] = useState<EligibleStudentOption | null>(null);
  const [effectiveDate, setEffectiveDate] = useState("");
  const [feePerClass, setFeePerClass] = useState("");
  const [scope, setScope] = useState<"this" | "future">("future");
  const [gradeOverride, setGradeOverride] = useState(false);

  // Initialize form state ONCE per open. Effective date is normalized to a single
  // YYYY-MM-DD string so formatting differences can never look like a change.
  useEffect(() => {
    if (open && row) {
      setSelected(null);
      setFeePerClass("");
      setScope("future");
      setGradeOverride(false);
      setEffectiveDate(row.scheduledDate ? dayjs(row.scheduledDate).format("YYYY-MM-DD") : dayjs().format("YYYY-MM-DD"));
    }
  }, [open, row]);

  // ONE stable, memoized query argument. Keyed ONLY by the class and the grade-
  // override toggle — never by the selected student, the dropdown input text, or
  // the option label. Client-side filtering handles search, so RTK Query sees the
  // same serialized args (and reuses one cache entry) until a real dependency
  // changes. This is what breaks the previous refetch loop.
  const eligibilityArgs = useMemo(
    () => ({ classId: row?.classId, all: gradeOverride }),
    [row?.classId, gradeOverride]
  );

  const {
    data: eligData,
    isLoading: eligLoading, // first load only (no data yet)
    isFetching,             // background refresh (e.g. override toggle) — keeps old data
    isError,
    refetch,
  } = useGetClassEligibleStudentsQuery(eligibilityArgs, { skip: !open || !row?.classId });

  const ctx = (eligData as any)?.data?.class ?? {};
  // `grade_locked` is a sibling of `class` on the payload (data.grade_locked),
  // not a field of the class context — read it from the right place so the
  // grade-scope hint and empty-state wording actually render.
  const gradeLocked: boolean = !!(eligData as any)?.data?.grade_locked;
  const options = useMemo(() => buildEligibleStudentOptions(eligData), [eligData]);

  // Keep the last non-empty option set visible during a background refetch so the
  // dropdown never blanks to "Loading…" and the selected value never disappears.
  const lastOptionsRef = useRef<EligibleStudentOption[]>([]);
  useEffect(() => {
    if (options.length) lastOptionsRef.current = options;
  }, [options]);
  const visibleOptions = options.length ? options : isFetching ? lastOptionsRef.current : options;

  // Guarantee the selected student is always in the option list so MUI can keep
  // rendering it (by id) even while a refresh is briefly in flight.
  const mergedOptions = useMemo(() => {
    if (selected && !visibleOptions.some((o) => o.id === selected.id)) return [selected, ...visibleOptions];
    return visibleOptions;
  }, [visibleOptions, selected]);

  // Clear the selection ONLY when a COMPLETED eligibility refresh proves it is no
  // longer eligible (e.g. turning grade override off drops an out-of-grade
  // student). Guarded on a finished, non-empty load so a transient in-flight
  // empty can never wrongly clear a valid pick. Because search is client-side,
  // `options` is the authoritative full eligible set — absence here is real.
  useEffect(() => {
    if (eligLoading || isFetching || options.length === 0 || !selected) return;
    if (!options.some((o) => o.id === selected.id)) setSelected(null);
  }, [options, eligLoading, isFetching, selected]);

  const submit = async () => {
    if (!selected) return;
    try {
      const res = await assignStudent({
        classId: row.classId,
        occurrenceId: scope === "this" ? row.id : undefined,
        studentId: selected.id,
        effectiveDate,
        feePerClass: feePerClass !== "" ? Number(feePerClass) : undefined,
        scope,
        gradeOverride,
      }).unwrap();
      if (res.status) {
        showMessage(res.message);
        onSuccess?.();
        onClose();
      } else showErrorMessage(res.message || "Failed to assign student");
    } catch (err: any) {
      showErrorMessage(err?.data?.message || "Failed to assign student");
    }
  };

  const enrolled = row?.studentCount ?? 0;
  const capacity = row?.capacity ?? null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, display: "flex", alignItems: "center", gap: 1 }}>
        <PersonAddAlt1OutlinedIcon fontSize="small" /> Assign Student
      </DialogTitle>
      <DialogContent>
        {/* Read-only class context */}
        <Box sx={{ p: 2, borderRadius: "10px", bgcolor: alpha("#0f172a", 0.02), border: "1px solid rgba(15,23,42,0.08)", mb: 2.5 }}>
          {/* Class name header — shown ONLY when the class has a real
             `assign_classes.name` (backend ClassLabel::format prefers
             it). If the backend fell back to the "grade × subject"
             composite (which compresses to "2, 3 +4 more · English and
             Math, English and Science +8 more" for broad classes) we
             hide it — the full grade→subjects mapping is rendered
             explicitly in the block below. */}
          {(() => {
            const raw = String(ctx.title || row?.className || "").trim();
            const isComposite = /\+\d+\s*more|·/.test(raw);
            return raw && !isComposite ? (
              <Typography sx={{ fontSize: 13.5, fontWeight: 700, mb: 1 }}>{raw}</Typography>
            ) : null;
          })()}
          <Grid container spacing={1.5}>
            <Grid item xs={6} sm={4}><Ctx label="Staff" value={ctx.staff_name || row?.staffName} /></Grid>
            <Grid item xs={6} sm={4}><Ctx label="Mode" value={ctx.mode_label || row?.classMode} /></Grid>
            <Grid item xs={6} sm={4}><Ctx label="Location" value={ctx.location_name || row?.locationName} /></Grid>
            <Grid item xs={6} sm={4}><Ctx label="Day / Time" value={`${ctx.day || ""} ${ctx.start_time || row?.startTime || ""}–${ctx.end_time || row?.endTime || ""}`.trim()} /></Grid>
            <Grid item xs={6} sm={4}><Ctx label="Capacity" value={capacity ?? "—"} /></Grid>
            <Grid item xs={6} sm={4}><Ctx label="Enrolled" value={enrolled} /></Grid>
            <Grid item xs={6} sm={4}><Ctx label="Available" value={capacity != null ? Math.max(0, capacity - enrolled) : "—"} /></Grid>
          </Grid>

          {/* Grade → Subjects grouped display. Uses the canonical
             structured field from the API (`grade_subjects`), not the
             flat `subjects`/`grades` arrays which — when rendered as
             React children directly — produced the concatenation soup
             this section previously showed. */}
          {Array.isArray(ctx.grade_subjects) && ctx.grade_subjects.length > 0 && (
            <Box sx={{ mt: 1.5 }}>
              <Typography sx={{ fontSize: 10, fontWeight: 700, color: "text.secondary", textTransform: "uppercase", mb: 0.75 }}>
                Grade / Subjects
              </Typography>
              <GradeSubjectGroups groups={ctx.grade_subjects} dense />
            </Box>
          )}
        </Box>

        {isError ? (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5, p: 1.5, mb: 1.5, borderRadius: "10px", bgcolor: alpha("#ef4444", 0.06), border: "1px solid", borderColor: alpha("#ef4444", 0.25) }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "#b91c1c" }}>
              <ErrorOutlineIcon fontSize="small" />
              <Typography sx={{ fontSize: 12.5, fontWeight: 600 }}>Unable to load eligible students.</Typography>
            </Box>
            <Button size="small" onClick={() => refetch()} sx={{ textTransform: "none", fontWeight: 700 }}>Retry</Button>
          </Box>
        ) : (
          <Autocomplete
            size="small"
            options={mergedOptions}
            loading={eligLoading}
            value={selected}
            getOptionLabel={(o: EligibleStudentOption) => o.label}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            filterOptions={(opts, s) => filterEligibleStudents(opts, s.inputValue)}
            onChange={(_, opt) => setSelected(opt)}
            renderInput={(p) => (
              <TextField
                {...p}
                label="Student *"
                placeholder="Search eligible students…"
                InputProps={{
                  ...p.InputProps,
                  endAdornment: (
                    <>
                      {isFetching && !eligLoading ? <CircularProgress size={16} color="inherit" /> : null}
                      {p.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            sx={{ mb: 1 }}
            noOptionsText={
              eligLoading
                ? "Loading…"
                : gradeLocked
                ? "No eligible students for this grade"
                : "No eligible students are available for this class."
            }
          />
        )}
        {gradeLocked && (
          <Typography sx={{ fontSize: 11, color: "text.secondary", mb: 1.5 }}>
            Showing only students in this class's grade{canOverrideGrade ? "." : " (grade override not permitted)."}
          </Typography>
        )}
        {canOverrideGrade && (
          <FormControlLabel
            control={<Checkbox size="small" checked={gradeOverride} onChange={(e) => setGradeOverride(e.target.checked)} />}
            label={<Typography sx={{ fontSize: 12.5 }}>Override grade eligibility (show all students)</Typography>}
            sx={{ mb: 1 }}
          />
        )}

        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12} sm={6}>
            <AppDatePicker label="Effective start date" size="small" fullWidth value={effectiveDate} onChange={setEffectiveDate} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Fee override (optional)" type="number" size="small" fullWidth value={feePerClass} onChange={(e) => setFeePerClass(e.target.value)}
              placeholder={selected?.perClassRate != null ? String(selected.perClassRate) : "Default"} InputProps={{ startAdornment: <Typography sx={{ mr: 0.5, color: "text.disabled" }}>$</Typography> }} />
          </Grid>
        </Grid>

        <Typography sx={{ fontSize: 12.5, fontWeight: 700, mt: 2, mb: 0.5 }}>Apply to</Typography>
        <RadioGroup value={scope} onChange={(e) => setScope(e.target.value as any)}>
          <FormControlLabel value="this" control={<Radio size="small" />} disabled={!row?.id} label={<Typography sx={{ fontSize: 13 }}>This occurrence only</Typography>} />
          <FormControlLabel value="future" control={<Radio size="small" />} label={<Typography sx={{ fontSize: 13 }}>This and future occurrences</Typography>} />
        </RadioGroup>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} sx={{ color: "text.secondary" }}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={isAssigning || !selected || !effectiveDate}
          startIcon={isAssigning ? <CircularProgress size={16} color="inherit" /> : <PersonAddAlt1OutlinedIcon />}>
          {isAssigning ? "Assigning..." : "Assign Student"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignStudentDialog;
